import './InsightsAnnouncementPopup.scss';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../contexts/account/AccountContexts';
import { INSIGHTS } from '../../../routes/path';
import AnnouncementPopup from '../announcement-popup/AnnouncementPopup';
import { useAnnouncement } from '../announcement-popup/useAnnouncement';
import { projectTabPath } from '../announcement-popup/projectTabPath';

// Per-user "seen" entry in the `onboarding` table — same store and flow as AdsAnnouncementPopup (shell,
// visibility hook and CTA path shared in `announcement-popup/`). Mock: https://insights20.lovable.app/.
const ENTRY = 'insights-tab-announcement';

// The mock's four feature rows with its Lucide icons (added to the `ico--*` sprite for this popup).
const FEATURES = [
  { key: 'citationGap', icon: 'ico--chart-column' },
  { key: 'perPrompt', icon: 'ico--grid-3x3' },
  { key: 'funnel', icon: 'ico--funnel' },
  { key: 'trends', icon: 'ico--chart-line' },
] as const;

const InsightsAnnouncementPopup: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();

  // Gate (user decision 2026-10-05): the logged-in account's INSIGHTS addon and not being on the Analytics
  // tab itself — no paid/trial-age rule like Ads. The account's OWN addon, deliberately without the
  // super-admin bypass of the module gate (support gets no promo).
  const hasInsightsAddon = !!accountResponse?.addons?.includes('INSIGHTS');
  const isOnInsightsPage =
    location.pathname === INSIGHTS || location.pathname.startsWith(`${INSIGHTS}/`);

  const { visible, dismiss } = useAnnouncement({
    entry: ENTRY,
    enabled: hasInsightsAddon,
    isOnTab: isOnInsightsPage,
  });

  const explore = () => {
    dismiss();
    navigate(projectTabPath(INSIGHTS, location.pathname, projectResponse));
  };

  if (!hasInsightsAddon || isOnInsightsPage) {
    return null;
  }

  return (
    <AnnouncementPopup
      visible={visible}
      onDismiss={dismiss}
      onCta={explore}
      className="announcement-popup--panel insights-announcement"
      badge={t('insightPage.announcement.badge')}
      title={t('insightPage.announcement.title')}
      description={t('insightPage.announcement.desc')}
      laterLabel={t('insightPage.announcement.later')}
      ctaLabel={t('insightPage.announcement.cta')}
    >
      <ul className="insights-announcement__features">
        {FEATURES.map(({ key, icon }) => (
          <li key={key} className="insights-announcement__feature">
            <span className="insights-announcement__feature-icon">
              <span className={`${icon} ico--xs`} aria-hidden="true" />
            </span>
            <div>
              <strong className="insights-announcement__feature-title">
                {t(`insightPage.announcement.${key}Title`)}
              </strong>
              <p className="insights-announcement__feature-desc">
                {t(`insightPage.announcement.${key}Desc`)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </AnnouncementPopup>
  );
};

export default InsightsAnnouncementPopup;
